/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Drawer } from "antd"
import { Button, Form, Input } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { HTTPStatus } from "constants/endpoints"
import { Config } from "constants/config"
import { formHasErrors, isMartBusinessType } from "utils/misc"
import { inputName } from "constants/menu_group"
import { applyUpdateMenuGroupClientError } from "utils/menu_group"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import { LanguageFullName } from "types/models/menu"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import {
  useLazyFetchMenuGroupDetailQuery,
  useUpdateMenuGroupMutation,
} from "queries/menuApi"
import { fetchMenuGroupDetailWithMigration } from "utils/rtk_migration_helper"
import { drawerTitleStyle, drawerFooter } from "../styles"
import {
  menuGroupDrawerFormContainer,
  menuGroupDrawerMenuConfirmButton,
  menuGroupDrawerStyle,
} from "./styles"

export interface MenuGroupDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupDrawerComponent: FC<MenuGroupDrawerProps> = ({
  visible,
  onClose,
  form,
}: MenuGroupDrawerProps) => {
  const {
    getFieldDecorator,
    getFieldValue,
    validateFields,
    setFieldsValue,
    isFieldsTouched,
    getFieldsError,
    setFields,
  } = form
  const dispatch = useDispatch()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: {
      menuGroupID,
      name: menuGroupName,
      businessType,
      languageSettings,
    },
    enableMenuTranslationPortal,
  } = useSelector((state: AppState) => state.menu)
  const {
    messages: { error: globalErrorMessage },
  } = useSelector((state: AppState) => state.global)
  const { maxLengthMenuGroupName } = Config
  const { menuGroupNameField } = inputName

  const {
    flags: {
      updateMenuGroup: isRtkUpdateMenuGroupEnabled,
      fetchMenuGroupDetail: isRtkFetchMenuGroupDetailEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [updateMenuGroupRtk] = useUpdateMenuGroupMutation()
  const [fetchMenuGroupDetailRtk] = useLazyFetchMenuGroupDetailQuery()

  const textMap = useTranslationsMap({
    title: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.editNameTitle"
      : "menu.menuGroup.editName.forMenu.editNameTitle",
    menuGroupNameLabel: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.editNameInputLabel"
      : "menu.menuGroup.editName.forMenu.editNameInputLabel",
    save: "common.save",
    menuNameCharExceeded: "menu.menuGroup.menuName.charLengthExceeded",
    menuNameAlreadyExist: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.nameExist"
      : "menu.menuGroup.editName.forMenu.nameExist",
  })

  const handleDrawerClose = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupEdit,
      action: EventAction.clickExit,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
      },
    })
    onClose()
  }, [menuGroupID, onClose])

  const handleSubmitFrom = useCallback(() => {
    const sendRtkMigrationTracking = (fromRtkQuery: boolean) => {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.updateMenuGroup,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isRtkUpdateMenuGroupEnabled,
          FROM_RTK_QUERY: fromRtkQuery,
          FROM_OLD_REDUX: !fromRtkQuery,
          ACTION_NAME: "updateMenuGroup",
        },
      })
    }

    validateFields(async (err, values) => {
      if (!err && menuGroupID) {
        let labels: Record<string, unknown> = {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
          STATUS: "SUCCESS",
        }

        // While migration flags are loading, do not fall back to old Redux: the enabled bit is
        // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
        // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
        // the trigger UI while loading so clicks are not silently dropped.
        if (!isRtkFlagLoading && isRtkUpdateMenuGroupEnabled) {
          const result = await updateMenuGroupRtk({
            merchantGroupID,
            menuGroupID,
            menuGroupName: values.menuGroupName,
          })

          if (result.error) {
            const rtkError = result.error as {
              status?: number
              data?: { message?: string }
            }
            const errCode =
              typeof rtkError.status === "number"
                ? rtkError.status
                : HTTPStatus.InternalServerError
            const errMessage = rtkError.data?.message ?? ""

            applyUpdateMenuGroupClientError({
              errCode,
              errMessage,
              menuGroupName: values.menuGroupName,
              menuGroupNameField,
              maxLengthMenuGroupName,
              globalErrorMessage,
              textMap,
              setFields,
            })

            labels = {
              ...labels,
              STATUS: "FAILURE",
              ...getResponseErrorEventLabelsForRTK(result),
            }
          } else {
            const fetchResult = await fetchMenuGroupDetailWithMigration({
              dispatch,
              fetchMenuGroupDetailRtk,
              isRtkFetchMenuGroupDetailEnabled,
              request: {
                menuGroupID,
                merchantGroupID,
              },
            })
            if (fetchResult?.type && !fetchResult.type.endsWith("_FAIL"))
              onClose()
          }

          sendRtkMigrationTracking(true)
        } else if (!isRtkFlagLoading) {
          const resp: any = await dispatch(
            actionCreator.menu.updateMenuGroup({
              merchantGroupID,
              menuGroupID,
              menuGroupName: values.menuGroupName,
            }),
          )
          const responseCode = resp?.payload?.status
          const errMessage = resp?.error?.response?.data?.message
          const errCode = resp?.error?.response?.status

          // Update success, refetch menu group detail
          // and close drawer
          if (responseCode === HTTPStatus.NoContent) {
            const fetchResult = await fetchMenuGroupDetailWithMigration({
              dispatch,
              fetchMenuGroupDetailRtk,
              isRtkFetchMenuGroupDetailEnabled,
              request: {
                menuGroupID,
                merchantGroupID,
              },
            })
            if (fetchResult?.type && !fetchResult.type.endsWith("_FAIL"))
              onClose()
          } else {
            applyUpdateMenuGroupClientError({
              errCode,
              errMessage,
              menuGroupName: values.menuGroupName,
              menuGroupNameField,
              maxLengthMenuGroupName,
              globalErrorMessage,
              textMap,
              setFields,
            })
          }

          // Track error
          if (resp?.type?.endsWith("_FAIL")) {
            labels = {
              ...labels,
              STATUS: "FAILURE",
              ...getResponseErrorEventLabels(resp),
            }
          }

          sendRtkMigrationTracking(false)
        } else {
          return
        }

        TroyWebTracker.sendEvent({
          category: EventCategory.menuGroupEdit,
          action: EventAction.clickSave,
          labels,
        })
      }
    })
  }, [
    menuGroupID,
    merchantID,
    merchantGroupID,
    dispatch,
    globalErrorMessage,
    isRtkFlagLoading,
    isRtkUpdateMenuGroupEnabled,
    isRtkFetchMenuGroupDetailEnabled,
    updateMenuGroupRtk,
    fetchMenuGroupDetailRtk,
    onClose,
    setFields,
    maxLengthMenuGroupName,
    menuGroupNameField,
    textMap.menuNameAlreadyExist,
    textMap.menuNameCharExceeded,
    validateFields,
  ])

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEdit,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible])

  // Set menu group name by default when drawer first open
  useEffect(() => {
    if (menuGroupName && !isFieldsTouched()) {
      setFieldsValue({ menuGroupName })
    }
  }, [menuGroupName, visible, setFieldsValue, isFieldsTouched])

  const disableSubmitBtn =
    isRtkFlagLoading ||
    !getFieldValue(menuGroupNameField) ||
    getFieldValue(menuGroupNameField) === menuGroupName ||
    formHasErrors(getFieldsError())

  const supportedLanguage =
    (enableMenuTranslationPortal &&
      languageSettings?.menuSupportedLanguages?.map(languageKey => {
        return languageSettings?.languageFullNames?.[
          languageKey as keyof LanguageFullName
        ]
      })) ||
    []

  return (
    <Fragment>
      <Drawer
        title={<span css={drawerTitleStyle}>{textMap.title}</span>}
        placement="right"
        width={450}
        visible={visible}
        onClose={handleDrawerClose}
        destroyOnClose
        css={menuGroupDrawerStyle}
      >
        <div css={menuGroupDrawerFormContainer}>
          <Form.Item label={textMap.menuGroupNameLabel}>
            {getFieldDecorator(menuGroupNameField, {
              rules: [
                {
                  max: maxLengthMenuGroupName,
                  message: textMap.menuNameCharExceeded.replace(
                    "<x>",
                    `${maxLengthMenuGroupName}`,
                  ),
                },
              ],
            })(<Input data-testid="menu-group-drawer-name-input" />)}
          </Form.Item>
          {enableMenuTranslationPortal && (
            <div>
              <Form.Item label="Default Language">
                <Input
                  data-testid="menu-group-drawer-name-input"
                  value={
                    languageSettings?.languageFullNames?.[
                      languageSettings?.menuDefaultLanguage as keyof LanguageFullName
                    ]
                  }
                  disabled
                />
              </Form.Item>
              <Form.Item label="Supported Languages">
                <Input
                  data-testid="menu-group-drawer-name-input"
                  value={supportedLanguage.join(", ")}
                  disabled
                />
              </Form.Item>
            </div>
          )}
        </div>
        <div css={drawerFooter}>
          <Button
            data-testid="menu-group-drawer-save-button"
            type="primary"
            css={menuGroupDrawerMenuConfirmButton}
            disabled={disableSubmitBtn}
            onClick={handleSubmitFrom}
          >
            {textMap.save}
          </Button>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const MenuGroupDrawer = Form.create<MenuGroupDrawerProps>({})(
  MenuGroupDrawerComponent,
)
